import React from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useAuthenticatedFetch } from './useAuth'
import type { DashboardOverview, FolderNode, DiagramSummary } from '../domains/dashboard/models'
import type { FolderChildren, FolderData } from './useFolderData'
import { Role } from '../core/repository/DiagramRepository'
import { useDashboardModal, useDashboardSelection } from '../stores/dashboardStore'
import { Logger } from '../utils/logger';
import {
  expandPurgeIds,
  installPurgeCacheSanitizer,
  OVERVIEW_QUERY_KEY,
  sanitizeDashboardCaches,
  sanitizeOverviewData,
  selectOverviewWithPurgeTombstones,
  usePurgePendingStore,
  type PurgeVars,
} from '../dashboard/purgePendingStore'
import { markOverviewStaleAfterFolderCreation } from '../dashboard/utils/dashboardOverviewCache'
import { getFolderDeletionFallbackPath } from '../dashboard/utils/folderDeletionNavigation'
import { findKnownDiagram as resolveKnownDiagram } from '../dashboard/utils/findKnownDiagram'
import { useRestoreDiagramOriginDialog } from '../dashboard/stores/restoreDiagramOriginStore'
import { useTranslation } from './useTranslation'
import { toast } from 'sonner'
import { useUpgradeModalStore } from '../stores/upgradeModalStore'
import { PlanUpgradeError } from '../domains/shared/utils/planUpgradeError'

export type { PurgeVars }

export type RestoreVars = {
  parentId?: string | null
  destinationFolderId?: string
  folderIds?: string[]
  diagramIds?: string[]
  restoreOriginFolderThenDiagrams?: boolean
}

type DiagramRestoreOriginCode =
  | 'ARCHIVE_ORIGIN_MISSING'
  | 'ARCHIVE_ORIGIN_ARCHIVED'
  | 'ARCHIVE_ORIGIN_UNAVAILABLE'

class DiagramRestoreOriginFailure extends Error {
  readonly code: DiagramRestoreOriginCode
  readonly originFolderId: string | null
  readonly originFolderName: string | null
  readonly diagramIds: string[]

  constructor(input: {
    code: DiagramRestoreOriginCode
    originFolderId: string | null
    originFolderName: string | null
    diagramIds: string[]
  }) {
    super(input.code)
    this.name = 'DiagramRestoreOriginFailure'
    this.code = input.code
    this.originFolderId = input.originFolderId
    this.originFolderName = input.originFolderName
    this.diagramIds = input.diagramIds
  }
}

const isDiagramRestoreOriginCode = (value: unknown): value is DiagramRestoreOriginCode => (
  value === 'ARCHIVE_ORIGIN_MISSING'
  || value === 'ARCHIVE_ORIGIN_ARCHIVED'
  || value === 'ARCHIVE_ORIGIN_UNAVAILABLE'
)

const normalizeRestoreVars = (
  vars?: RestoreVars | string | null,
): RestoreVars => {
  if (vars == null || typeof vars === 'string') {
    return { parentId: vars ?? null }
  }
  return vars
}

const logger = Logger.getLoggerWithPrefix('Dashboard');

const personalOrgId = '00000000-0000-0000-0000-000000000000'

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T
}

function walkFolders(rootFolders: FolderNode[], fn: (node: FolderNode, parent: FolderNode | null) => void) {
  const stack: Array<{ node: FolderNode; parent: FolderNode | null }> = rootFolders.map(n => ({ node: n, parent: null }))
  while (stack.length) {
    const { node, parent } = stack.pop()!
    fn(node, parent)
    for (let i = 0; i < node.children.length; i++) stack.push({ node: node.children[i], parent: node })
  }
}

function findFolderById(roots: FolderNode[], id: string): FolderNode | null {
  let found: FolderNode | null = null
  walkFolders(roots, (n) => { if (n.id === id) found = n })
  return found
}

function removeFolderById(roots: FolderNode[], id: string): { removed: FolderNode | null; newRoots: FolderNode[] } {
  const copy = clone(roots)
  let removed: FolderNode | null = null
  function dfs(nodes: FolderNode[]): FolderNode[] {
    const out: FolderNode[] = []
    for (const n of nodes) {
      if (n.id === id) { removed = n; continue }
      const c = { ...n, children: dfs(n.children) }
      out.push(c)
    }
    return out
  }
  const newRoots = dfs(copy)
  return { removed, newRoots }
}

function isDescendantOfAny(roots: FolderNode[], targetId: string, ancestorIds: string[]): boolean {
  if (!targetId || !ancestorIds.length) return false
  const ancestorSet = new Set(ancestorIds)
  let found = false
  walkFolders(roots, (node) => {
    if (found) return
    if (ancestorSet.has(node.id)) {
      walkFolders(node.children, (child) => {
        if (child.id === targetId) found = true
      })
    }
  })
  return found
}

function removeDiagramById(roots: FolderNode[], id: string): { removed: DiagramSummary | null; newRoots: FolderNode[] } {
  const copy = clone(roots)
  let removed: DiagramSummary | null = null
  function dfs(nodes: FolderNode[]): FolderNode[] {
    return nodes.map(n => {
      const diagrams = n.diagrams.filter(d => {
        if (d.id === id) { removed = d; return false }
        return true
      })
      return { ...n, diagrams, children: dfs(n.children) }
    })
  }
  const newRoots = dfs(copy)
  return { removed, newRoots }
}

export function useDashboard() {
  const authenticatedFetch = useAuthenticatedFetch()
  const { t } = useTranslation('diagram')
  const qc = useQueryClient()
  const modal = useDashboardModal()
  const sel = useDashboardSelection()
  const navigate = useNavigate()
  const routerState = useRouterState()
  const key = OVERVIEW_QUERY_KEY
  const purgeRevision = usePurgePendingStore((state) => state.revision)

  React.useEffect(() => {
    installPurgeCacheSanitizer(qc)
  }, [qc])

  // Extract current folder ID from route
  const getCurrentFolderId = (): string | null => {
    let p = routerState.location.pathname || '/'
    if (p.startsWith('/dashboard')) {
      p = p.slice('/dashboard'.length) || '/'
    }
    const seg = p.split('/').filter(Boolean)[0] || ''
    // Return folder ID only if it's not a special section route
    if (seg && seg !== 'recent' && seg !== 'personal' && seg !== 'myorg' && seg !== 'archived') {
      return seg
    }
    return null
  }

  const overview = useQuery<DashboardOverview>({
    queryKey: key,
    queryFn: async () => {
      try {
        const res = await authenticatedFetch('/api/dashboard/overview')
        if (!res.ok) throw new Error('Failed to load dashboard')
        const data = await res.json()
        // TODO: remove this log before move to prod
        logger.info('[Dashboard] Overview', {
          orgId: data.orgId,
          userId: data.userId,
          rootCount: data.org?.roots?.length ?? 0,
          recentCount: data.recent?.length ?? 0,
        })
        return sanitizeOverviewData(data)
      } catch {
        throw new Error('Failed to load dashboard');
      }
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    select: React.useCallback(
      (data: DashboardOverview) => {
        void purgeRevision
        return selectOverviewWithPurgeTombstones(data)
      },
      [purgeRevision],
    ),
  })

  const getKnownDiagramIds = React.useCallback((): Set<string> => {
    const ids = new Set<string>()
    const currentOverview = qc.getQueryData<DashboardOverview>([...OVERVIEW_QUERY_KEY])

    if (currentOverview) {
      currentOverview.recent.forEach((diagram) => ids.add(diagram.id))
      const roots = [
        ...currentOverview.personal.roots,
        ...currentOverview.org.roots,
        ...currentOverview.sharedWithMe.roots,
        ...currentOverview.archived.roots,
        ...(currentOverview.externalOrgs || []).flatMap((org) => org.roots),
      ]
      walkFolders(roots, (folder) => {
        folder.diagrams.forEach((diagram) => ids.add(diagram.id))
      })
    }

    // Initial overview mode deliberately omits diagrams from folder trees.
    // Folder contents are authoritative in their lazy-loaded children caches.
    for (const [queryKey, value] of qc.getQueriesData({ queryKey: ['folder'] })) {
      if (!value) continue
      if (queryKey[2] === 'children') {
        const children = value as FolderChildren
        children.diagrams.forEach((diagram) => ids.add(diagram.id))
        continue
      }
      if (queryKey[2] === 'diagrams') {
        const diagrams = (value as { diagrams?: Array<{ id: string }> }).diagrams
        diagrams?.forEach((diagram) => ids.add(diagram.id))
      }
    }

    return ids
  }, [qc])

  const findKnownDiagram = React.useCallback((diagramId: string) => {
    const currentOverview = qc.getQueryData<DashboardOverview>([...OVERVIEW_QUERY_KEY])
    return resolveKnownDiagram(
      diagramId,
      currentOverview,
      qc.getQueriesData({ queryKey: ['folder'] }),
    )
  }, [qc])

  const rename = useMutation({
    mutationFn: async (name: string) => {
      if (!name) return
      const folderIds = [...sel.selectedFolderIds]
      const diagramIds = [...sel.selectedDiagramIds]
      
      // Include modal.target if present
      if (modal.target) {
        if (modal.target.kind === 'folder' && !folderIds.includes(modal.target.id)) {
          folderIds.push(modal.target.id)
        } else if (modal.target.kind === 'diagram' && !diagramIds.includes(modal.target.id)) {
          diagramIds.push(modal.target.id)
        }
      }
      
      await Promise.all([
        ...folderIds.map((id) => authenticatedFetch(`/api/folders/${id}`, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ name }) })),
        ...diagramIds.map((id) => authenticatedFetch(`/api/diagrams/${id}`, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ name }) })),
      ])
    },
    onMutate: async (name: string) => {
      if (!name) return { prev: undefined }
      const ensuredName = name
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<DashboardOverview>(key)
      if (prev) {
        const next = clone(prev)
        
        // Collect all folder IDs to rename
        const renameFolderIds = new Set(sel.selectedFolderIds)
        if (modal.target?.kind === 'folder') {
          renameFolderIds.add(modal.target.id)
        }
        
        // Collect all diagram IDs to rename
        const renameDiagramIds = new Set(sel.selectedDiagramIds)
        if (modal.target?.kind === 'diagram') {
          renameDiagramIds.add(modal.target.id)
        }
        
        // Helper to rename folders in any root array
        const renameFoldersInRoots = (roots: FolderNode[]) => {
          walkFolders(roots, (n) => { if (renameFolderIds.has(n.id)) n.name = ensuredName })
        }
        
        // Helper to rename diagrams in any root array
        const renameDiagramsInRoots = (roots: FolderNode[]) => {
          function renameDiagrams(nodes: FolderNode[]) {
            nodes.forEach(n => {
              n.diagrams.forEach(d => { if (renameDiagramIds.has(d.id)) d.name = ensuredName })
              renameDiagrams(n.children)
            })
          }
          renameDiagrams(roots)
        }
        
        // Update all root types
        renameFoldersInRoots(next.personal.roots)
        renameFoldersInRoots(next.org.roots)
        renameFoldersInRoots(next.sharedWithMe.roots)
        renameFoldersInRoots(next.archived.roots)
        if (next.externalOrgs) {
          next.externalOrgs.forEach(org => renameFoldersInRoots(org.roots))
        }
        
        // Update diagrams in all root types
        renameDiagramsInRoots(next.personal.roots)
        renameDiagramsInRoots(next.org.roots)
        renameDiagramsInRoots(next.sharedWithMe.roots)
        renameDiagramsInRoots(next.archived.roots)
        if (next.externalOrgs) {
          next.externalOrgs.forEach(org => renameDiagramsInRoots(org.roots))
        }
        
        // Update recent diagrams
        next.recent.forEach(d => { if (renameDiagramIds.has(d.id)) d.name = ensuredName })
        
        qc.setQueryData(key, next)
      }
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev) },
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ['folder'] }); modal.close(); sel.clearSelection() },
  })

  const archive = useMutation({
    mutationFn: async () => {
      // A silently-swallowed 403/500 here kept the optimistic move into
      // Archived, so the folder "came back" on the next real refetch
      // (QA issue 74 family).
      if (sel.selectedFolderIds.length) {
        const res = await authenticatedFetch('/api/folders/batch/archive', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ids: sel.selectedFolderIds }) })
        if (!res.ok) throw new Error('Failed to archive folders')
      }
      if (sel.selectedDiagramIds.length) {
        const res = await authenticatedFetch('/api/diagrams/batch/archive', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ids: sel.selectedDiagramIds }) })
        if (!res.ok) throw new Error('Failed to archive diagrams')
      }
    },
    onMutate: async () => {
      const currentFolderId = getCurrentFolderId()
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<DashboardOverview>(key)
      const allRoots = prev ? [...prev.personal.roots, ...prev.org.roots, ...prev.sharedWithMe.roots, ...(prev.externalOrgs || []).flatMap(o => o.roots)] : []
      const shouldNavigate = currentFolderId
        ? sel.selectedFolderIds.includes(currentFolderId) || isDescendantOfAny(allRoots, currentFolderId, sel.selectedFolderIds)
        : false
      if (shouldNavigate) {
        navigate({ to: getFolderDeletionFallbackPath(prev, currentFolderId!) as never })
      }
      if (prev) {
        qc.setQueryData(key, (current: DashboardOverview | undefined) => {
          if (!current) return current
          const next = clone(current)
          const allSections = [
            { key: 'personal', roots: next.personal.roots },
            { key: 'org', roots: next.org.roots },
            { key: 'sharedWithMe', roots: next.sharedWithMe.roots },
          ]
          if (next.externalOrgs) {
            next.externalOrgs.forEach(o => allSections.push({ key: `ext-${o.orgId}`, roots: o.roots }))
          }
          const archivedAdded: FolderNode[] = []
          const seenIds = new Set<string>()
          for (const id of sel.selectedFolderIds) {
            for (const section of allSections) {
              const result = removeFolderById(section.roots, id)
              section.roots = result.newRoots
              if (result.removed && !seenIds.has(result.removed.id)) {
                seenIds.add(result.removed.id)
                archivedAdded.push(result.removed)
              }
            }
          }
          for (const id of sel.selectedDiagramIds) {
            for (const section of allSections) {
              const result = removeDiagramById(section.roots, id)
              section.roots = result.newRoots
            }
          }
          next.personal.roots = allSections.find(s => s.key === 'personal')!.roots
          next.org.roots = allSections.find(s => s.key === 'org')!.roots
          next.sharedWithMe.roots = allSections.find(s => s.key === 'sharedWithMe')!.roots
          if (next.externalOrgs) {
            next.externalOrgs.forEach(o => {
              const section = allSections.find(s => s.key === `ext-${o.orgId}`)
              if (section) o.roots = section.roots
            })
          }
          next.archived.roots = [...(next.archived.roots ?? []), ...archivedAdded]
          next.recent = next.recent.filter(d => !sel.selectedDiagramIds.includes(d.id))
          return next
        })
      }
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev)
      toast.error(t('contextMenu.archiveFailed', { defaultValue: 'Could not archive. Please try again.' }))
    },
    onSuccess: () => {
      modal.close()
      sel.clearSelection()
      toast.success(t('contextMenu.archiveSuccess', { defaultValue: 'Archived' }))
    },
    // Invalidate on error too: a partially-committed batch (server archives
    // some ids, then 403s) must reconcile against the server, not against the
    // rolled-back optimistic state.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key })
      qc.invalidateQueries({ queryKey: ['folder'] })
    },
  })

  const restore = useMutation({
    mutationFn: async (rawVars?: RestoreVars | string | null) => {
      const vars = normalizeRestoreVars(rawVars)
      const parentId = vars.parentId ?? null
      const folderIds = vars.folderIds ?? sel.selectedFolderIds
      const diagramIds = vars.diagramIds ?? sel.selectedDiagramIds

      if (vars.restoreOriginFolderThenDiagrams) {
        if (!folderIds.length) throw new Error('Failed to restore folders')
        const folderRes = await authenticatedFetch('/api/folders/batch/restore', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ ids: folderIds, parentId: null }),
        })
        if (!folderRes.ok) throw new Error('Failed to restore folders')
        if (!diagramIds.length) return
        const diagramRes = await authenticatedFetch('/api/diagrams/batch/restore', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ ids: diagramIds }),
        })
        if (!diagramRes.ok) {
          const data = await diagramRes.json().catch(() => ({})) as {
            error?: string
            originFolderId?: string | null
            originFolderName?: string | null
          }
          if (diagramRes.status === 409 && isDiagramRestoreOriginCode(data.error)) {
            throw new DiagramRestoreOriginFailure({
              code: data.error,
              originFolderId: data.originFolderId ?? null,
              originFolderName: data.originFolderName ?? null,
              diagramIds,
            })
          }
          throw new Error('Failed to restore diagrams')
        }
        return
      }

      if (folderIds.length) {
        await authenticatedFetch('/api/folders/batch/restore', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ ids: folderIds, parentId }),
        })
      }
      if (diagramIds.length) {
        const body: { ids: string[]; destinationFolderId?: string } = { ids: diagramIds }
        if (vars.destinationFolderId) body.destinationFolderId = vars.destinationFolderId
        const res = await authenticatedFetch('/api/diagrams/batch/restore', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        })
        if (!res.ok) {
          const data = await res.json().catch(() => ({})) as {
            error?: string
            originFolderId?: string | null
            originFolderName?: string | null
          }
          if (res.status === 409 && isDiagramRestoreOriginCode(data.error)) {
            throw new DiagramRestoreOriginFailure({
              code: data.error,
              originFolderId: data.originFolderId ?? null,
              originFolderName: data.originFolderName ?? null,
              diagramIds,
            })
          }
          throw new Error('Failed to restore diagrams')
        }
      }
    },
    onMutate: async (rawVars?: RestoreVars | string | null) => {
      const vars = normalizeRestoreVars(rawVars)
      const parentId = vars.parentId ?? null
      const folderIds = vars.folderIds ?? sel.selectedFolderIds
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<DashboardOverview>(key)
      if (!prev || vars.destinationFolderId || vars.restoreOriginFolderThenDiagrams) {
        return { prev: undefined }
      }
      qc.setQueryData(key, (current: DashboardOverview | undefined) => {
        if (!current) return current
        const next = clone(current)
        for (const id of folderIds) {
          const { removed, newRoots } = removeFolderById(next.archived.roots, id)
          next.archived.roots = newRoots
          if (removed) {
            const restoredFolder = { ...removed, archived: false }
            const listUnderOrg = (restoredFolder as { listUnderOrg?: boolean }).listUnderOrg
            const value = (restoredFolder.orgId ?? '') as string
            const isPersonal = listUnderOrg === false || !value || value === personalOrgId
            if (parentId) {
              const roots = isPersonal ? next.personal.roots : next.org.roots
              const dest = findFolderById(roots, parentId)
              if (dest) {
                dest.children = [...dest.children, restoredFolder]
                if (isPersonal) next.personal.roots = [...next.personal.roots]
                else next.org.roots = [...next.org.roots]
              } else {
                if (isPersonal) next.personal.roots = [...next.personal.roots, restoredFolder]
                else next.org.roots = [...next.org.roots, restoredFolder]
              }
            } else {
              const roots = isPersonal ? next.personal.roots : next.org.roots
              const destParentId = restoredFolder.parentId ?? null
              const dest = destParentId ? findFolderById(roots, destParentId) : null
              if (dest) {
                dest.children = [...dest.children, restoredFolder]
                if (isPersonal) next.personal.roots = [...next.personal.roots]
                else next.org.roots = [...next.org.roots]
              } else {
                if (isPersonal) next.personal.roots = [...next.personal.roots, restoredFolder]
                else next.org.roots = [...next.org.roots, restoredFolder]
              }
            }
          }
        }
        return next
      })
      return { prev }
    },
    onError: (error, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev)
      if (error instanceof DiagramRestoreOriginFailure) {
        useRestoreDiagramOriginDialog.getState().openWith({
          reason: error.code,
          diagramIds: error.diagramIds,
          originFolderId: error.originFolderId,
          originFolderName: error.originFolderName,
        })
        return
      }
      toast.error(t('contextMenu.restoreFailed', { defaultValue: 'Could not restore. Please try again.' }))
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key })
      qc.invalidateQueries({ queryKey: ['folder'] })
      modal.close()
      sel.clearSelection()
    },
  })

  const purge = useMutation({
    mutationFn: async (vars: PurgeVars): Promise<{
      failedFolderIds: string[]
      deletedFolderIds: string[]
      accepted: Array<{ folderId: string; operationId: string; status: string; phase: string; failureCode: string | null }>
    }> => {
      let failedFolderIds: string[] = []
      let deletedFolderIds: string[] = []
      let accepted: Array<{ folderId: string; operationId: string; status: string; phase: string; failureCode: string | null }> = []
      if (vars.folderIds.length) {
        const res = await authenticatedFetch('/api/folders/batch/delete', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ ids: vars.folderIds }),
        })
        if (!res.ok && res.status !== 202) throw new Error('Failed to delete folders')
        const body = await res.json().catch(() => null) as
          | {
              failed?: Array<{ id: string }>
              deleted?: string[]
              accepted?: Array<{
                id: string
                operation: {
                  operationId: string
                  status: string
                  phase: string
                  failureCode: string | null
                }
              }>
            }
          | null
        failedFolderIds = (body?.failed ?? []).map((entry) => entry.id)
        deletedFolderIds = body?.deleted ?? []
        accepted = (body?.accepted ?? []).map((entry) => ({
          folderId: entry.id,
          operationId: entry.operation.operationId,
          status: entry.operation.status,
          phase: entry.operation.phase,
          failureCode: entry.operation.failureCode,
        }))
      }
      if (vars.diagramIds.length) {
        const res = await authenticatedFetch('/api/diagrams/batch/purge', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ ids: vars.diagramIds }),
        })
        if (!res.ok) throw new Error('Failed to purge diagrams')
      }
      return { failedFolderIds, deletedFolderIds, accepted }
    },
    // Keep the target visible until the server confirms the irreversible write.
    // Cache rewrites and navigation must not prevent the request from running.
    onMutate: () => {
      usePurgePendingStore.getState().beginPurge()
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: key })
      void qc.invalidateQueries({ queryKey: ['folder'] })
      toast.error(t('contextMenu.deletePermaFailed', { defaultValue: 'Could not delete. The item was restored.' }))
    },
    onSuccess: (data, vars) => {
      const failed = new Set(data.failedFolderIds)
      const acceptedIds = new Set(data.accepted.map((entry) => entry.folderId))
      const syncDeletedIds = data.deletedFolderIds.length
        ? data.deletedFolderIds.filter((id) => !failed.has(id) && !acceptedIds.has(id))
        : vars.folderIds.filter((id) => !failed.has(id) && !acceptedIds.has(id))
      const currentOverview = qc.getQueryData<DashboardOverview>(key)
      const currentFolderId = getCurrentFolderId()
      const store = usePurgePendingStore.getState()

      if (data.accepted.length) {
        const acceptedFolders = data.accepted.map((entry) => entry.folderId)
        const expandedAccepted = expandPurgeIds({ folderIds: acceptedFolders, diagramIds: [] }, currentOverview)
        store.markPending(expandedAccepted.folderIds, expandedAccepted.diagramIds)
        store.trackOperations(data.accepted)
      }

      if (syncDeletedIds.length || vars.diagramIds.length) {
        const confirmed = { folderIds: syncDeletedIds, diagramIds: vars.diagramIds }
        const committed = expandPurgeIds(confirmed, currentOverview)
        store.commitPurged(committed.folderIds, committed.diagramIds)
        if (currentFolderId && committed.folderIds.includes(currentFolderId)) {
          navigate({ to: getFolderDeletionFallbackPath(currentOverview, currentFolderId) as never })
        }
        sel.removeFromSelection(confirmed.folderIds, confirmed.diagramIds)
      }

      sanitizeDashboardCaches(qc)
      void qc.invalidateQueries({ queryKey: key })
      void qc.invalidateQueries({ queryKey: ['folder'] })
      modal.close()
    },
    onSettled: () => {
      usePurgePendingStore.getState().endPurge()
    },
  })

  const pendingOperations = usePurgePendingStore((state) => state.pendingOperations)

  const retryPurge = useMutation({
    mutationFn: async (operationId: string) => {
      const res = await authenticatedFetch(`/api/folder-purge-operations/${operationId}/retry`, {
        method: 'POST',
      })
      if (!res.ok && res.status !== 202) throw new Error('Failed to retry deletion')
      return await res.json() as {
        operationId: string
        rootFolderId: string
        status: string
        phase: string
        failureCode: string | null
      }
    },
    onSuccess: (receipt) => {
      const store = usePurgePendingStore.getState()
      const overview = qc.getQueryData<DashboardOverview>(key)
      const expanded = expandPurgeIds({ folderIds: [receipt.rootFolderId], diagramIds: [] }, overview)
      store.markPending(expanded.folderIds, expanded.diagramIds)
      store.trackOperations([{
        operationId: receipt.operationId,
        folderId: receipt.rootFolderId,
        status: receipt.status,
        phase: receipt.phase,
        failureCode: receipt.failureCode,
      }])
      sanitizeDashboardCaches(qc)
      toast.message(t('contextMenu.deletePermaDeleting', { defaultValue: 'Deleting…' }))
      void qc.invalidateQueries({ queryKey: key })
      void qc.invalidateQueries({ queryKey: ['folder'] })
    },
    onError: () => {
      toast.error(t('contextMenu.deletePermaFailed', { defaultValue: 'Could not delete. The item was restored.' }))
    },
  })

  // Fetch default legacy folder (owned by current user). Returns { folderId, folder }
  const getLegacyDefaultFolder = React.useCallback(async (): Promise<{ folderId: string; folder?: FolderData['folder'] }> => {
    const res = await authenticatedFetch('/api/folders/default', { method: 'GET' })
    if (res.status === 404) throw new Error('DEFAULT_NOT_FOUND')
    if (!res.ok) throw new Error('Unable to load Home. Please try again.')
    const result = await res.json()
    if (typeof result.folderId !== 'string' || !result.folderId.trim()) {
      throw new Error('Home did not return a folder. Please try again.')
    }
    return result
  }, [authenticatedFetch])

  type CreateFolderVars = { name: string; orgId: string; scope?: 'personal' | 'org'; parentId?: string | null; domain?: string }
  type CreateFolderResult = { folderId: string; redirect: string }
  const createFolder = useMutation({
    mutationFn: async (vars: CreateFolderVars): Promise<CreateFolderResult> => {
      const scopeParam = vars.scope ? `&scope=${encodeURIComponent(vars.scope)}` : ''
      const parentIdParam = vars.parentId ? `&parentId=${encodeURIComponent(vars.parentId)}` : ''
      const domainParam = vars.domain ? `&domain=${encodeURIComponent(vars.domain)}` : ''
      const url = `/api/folders/new?name=${encodeURIComponent(vars.name)}&orgId=${encodeURIComponent(vars.orgId)}${scopeParam}${parentIdParam}${domainParam}`
      const res = await authenticatedFetch(url)
      if (!res.ok) {
        // The server refuses a sub-folder on a plan without child folders with
        // an exact, actionable sentence ("Child folders require Enterprise or
        // Business plan"). Discarding the body turned that into a bare "Failed
        // to create folder" in the dialog, which reads as a system fault and
        // leaves the user retrying a request that can never succeed — the
        // reported symptom in QA issue 64. Depth and parent-permission refusals
        // are equally specific and were lost the same way.
        const data = await res.json().catch(() => ({})) as { error?: string }
        throw new Error(data.error?.trim() || 'Failed to create folder')
      }
      return res.json()
    },
    onSuccess: (created, vars) => {
      const old = qc.getQueryData<DashboardOverview>(key)
      if (!old) {
        qc.invalidateQueries({ queryKey: key })
        return
      }
      const newNode: FolderNode = {
        id: created.folderId,
        name: vars.name,
        orgId: vars.orgId,
        ownerUserId: old.userId,
        parentId: vars.parentId ?? null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        diagrams: [],
        children: [],
        source: 'native',
        role: Role.Owner,
        ...(vars.domain ? { domain: vars.domain } : {}),
      }
      if (vars.parentId) {
        const next = clone(old)
        const allSections: FolderNode[][] = [next.personal.roots, next.org.roots, next.sharedWithMe.roots]
        if (next.externalOrgs) next.externalOrgs.forEach(o => allSections.push(o.roots))
        for (const roots of allSections) {
          const parent = findFolderById(roots, vars.parentId)
          if (parent) {
            parent.children = [...parent.children, newNode]
            break
          }
        }
        qc.setQueryData(key, next)
      } else {
        const next: DashboardOverview = {
          ...old,
          [vars.scope === 'personal' ? 'personal' : 'org']: {
            roots: [
              ...((vars.scope === 'personal' ? old.personal.roots : old.org.roots) as FolderNode[]),
              newNode,
            ],
          },
        }
        qc.setQueryData(key, next)
      }
      qc.invalidateQueries({ queryKey: key })
      // The open folder view reads children from its own query with a 30s
      // staleTime — without this the new sub-folder is invisible until then.
      if (vars.parentId) qc.invalidateQueries({ queryKey: ['folder', vars.parentId] })
    },
  })

  type CreateLegacyFolderVars = { name: string; section?: 'org' | 'personal'; isDefault?: boolean; parentId?: string | null }
  const createLegacyFolder = useMutation({
    mutationFn: async (vars: CreateLegacyFolderVars): Promise<CreateFolderResult> => {
      const body: { name: string; section?: 'org'|'personal'; isDefault?: boolean; parentId?: string | null } = { name: vars.name }
      if (vars.section) body.section = vars.section
      if (typeof vars.isDefault === 'boolean') body.isDefault = vars.isDefault
      if (vars.parentId) body.parentId = vars.parentId
      const res = await authenticatedFetch('/api/folders/create-legacy', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const error = await res.json()
        if (res.status === 403 && error.code === 'PLAN_UPGRADE_REQUIRED') {
          useUpgradeModalStore.getState().openModal('create-folder', 'Upgrade to create additional diagram folders')
          throw new PlanUpgradeError(error.message || error.error || 'Failed to create folder', {
            code: error.code,
            feature: error.meta?.feature,
            planType: error.meta?.planType,
          })
        }
        throw new Error(error.message || error.error || 'Failed to create folder')
      }
      return res.json()
    },
    onSuccess: (created, vars) => {
      // Default Home is grouped by the server and is excluded from the folder
      // allowance. Never insert its physical ID as an ordinary visible folder.
      if (vars.isDefault) {
        qc.invalidateQueries({ queryKey: key })
        return
      }
      const old = qc.getQueryData<DashboardOverview>(key)
      if (!old) {
        qc.invalidateQueries({ queryKey: key })
        return
      }
      const newNode: FolderNode = {
        id: created.folderId,
        name: vars.name,
        orgId: old.orgId,
        ownerUserId: old.userId,
        parentId: vars.parentId ?? null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        diagrams: [],
        children: [],
        source: 'legacy',
        role: Role.Owner,
      }
      const next = clone(old)
      if (vars.parentId) {
        const allSections: FolderNode[][] = [next.personal.roots, next.org.roots, next.sharedWithMe.roots]
        if (next.externalOrgs) next.externalOrgs.forEach(o => allSections.push(o.roots))
        for (const roots of allSections) {
          const parent = findFolderById(roots, vars.parentId)
          if (parent) {
            parent.children = [...parent.children, newNode]
            break
          }
        }
      } else {
        const section = vars.section === 'personal' ? 'personal' : 'org'
        next[section].roots = [...next[section].roots, newNode]
      }
      qc.setQueryData(key, next)
      if (vars.parentId) qc.invalidateQueries({ queryKey: ['folder', vars.parentId] })
      // The create response already gave us the authoritative folder ID and
      // the shared overview cache now contains that node. Keep it available
      // for route selection instead of starting another request immediately.
      void markOverviewStaleAfterFolderCreation(qc, key)
    },
  })

  // Convert a folder to a vertical domain (or back to workspace with
  // domain=null). Metadata-only on the backend (S6); just refresh the
  // overview so the sidebar group + folder surfaces re-resolve.
  const convertFolder = useMutation({
    mutationFn: async (vars: { folderId: string; domain: string | null }): Promise<{ folder: { id: string; domain?: string } }> => {
      const res = await authenticatedFetch(`/api/folders/${vars.folderId}/convert`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ domain: vars.domain }),
      })
      if (!res.ok) throw new Error('Failed to convert folder')
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key })
    },
  })

  // Get max folder depth
  const maxDepthQuery = useQuery<{ maxDepth: number }>({
    queryKey: ['folder', 'max-depth'],
    queryFn: async () => {
      const res = await authenticatedFetch('/api/folders/max-depth')
      if (!res.ok) throw new Error('Failed to fetch max depth')
      return res.json()
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  return { 
    overview, 
    rename, 
    archive, 
    restore, 
    purge,
    retryPurge,
    pendingOperations,
    createFolder,
    createLegacyFolder,
    convertFolder,
    getLegacyDefaultFolder,
    getKnownDiagramIds,
    findKnownDiagram,
    maxDepth: maxDepthQuery.data?.maxDepth ?? 5, // Default to 5 if not loaded yet
  }
}
